<template>
    <lni-disclosure
        :id="`${id}_accordion`"
        class="--accordion"
        @toggle="onToggle"
    >
        <template slot="indicator">
            <span
                aria-hidden="true"
                class="lnicon--caret--right lni-u-type--lg lni-u-ma0 lni-u-text--center"
            />
        </template>
        <span
            slot="toggle"
            class="lni-u-heading--2 lni-u-pl1"
        >
            {{ content?.workloadHeading }}
        </span>
        <template slot="content">
            <div class="--row lni-u-gap2 lni-u-mt2">
                <div class="lni-c-box--content --card --col__1of3">
                    <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                        {{ content?.stepsTotalLabel }}
                    </p>
                    <span class="--label --number">
                        {{ stepsTotal }}
                    </span>
                </div>
                <div class="lni-c-box--content --card --col__2of3">
                    <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                        {{ content?.stepsShiftLabel }}
                    </p>
                    <div class="lni-u-flex">
                        <span class="--label">
                            {{ cdcStepText }}
                        </span>
                        <lni-janitorial-info-modal
                            :id="`${id}_steps-info`"
                            :title="content?.cdcStepTitle"
                            :body="content?.cdcStepMessage"
                        />
                    </div>
                </div>
            </div>
            <lni-janitorial-complaint-card
                v-if="isManagementReport || isErgonomicEvaluation"
                :id="`${id}_walking-complaint`"
                classString="lni-u-mt2"
                :complaint="walkingComplaint"
            >
                <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                    {{ content?.complaintPrefix }}
                </p>
                <span class="--label">
                    {{ walkingComplaint ? '' : content?.notText }} {{ content?.walkingComplaintText }}
                </span>
                <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mt1">
                    {{ content?.walkingComplaintPostfix }}
                </p>
            </lni-janitorial-complaint-card>
            <lni-janitorial-discomfort-level
                v-if="isErgonomicEvaluation"
                :id="`${id}_lower-extremity_discomfort`"
                classString="lni-u-mt2"
                :name="content?.extremityLabel"
                :shiftStartDiscomfort="lowerExtremityDiscomfortStart"
                :shiftEndDiscomfort="lowerExtremityDiscomfortEnd"
            />
            <div class="--row lni-u-gap2 lni-u-mt3">
                <div
                    :class="[
                        'lni-c-box--content --card',
                        { '--col__2of4': isErgonomicEvaluation },
                    ]"
                >
                    <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                        {{ content?.energyDemandText }}
                    </p>
                    <span class="--label">
                        {{ energyText(mawtRatioTotal) }}
                    </span>
                </div>
                <div
                    v-if="isErgonomicEvaluation"
                    class="lni-c-box--content --card --col__2of4"
                >
                    <div class="lni-u-flex lni-u-mb1">
                        <p class="lni-u-type--xs lni-u-line-height--tight">
                            {{ content?.mawtHeading }}
                        </p>
                        <lni-janitorial-info-modal
                            :id="`${id}_mawt-info`"
                            classString="lni-u-flex"
                            :title="content?.mawtHeading"
                            :body="content?.mawtMessage"
                        />
                    </div>
                    <span class="--label --number">
                        {{ mawtRatioTotal }}
                    </span>
                </div>
            </div>
            <lni-janitorial-complaint-card
                v-if="isManagementReport || isErgonomicEvaluation"
                :id="`${id}_overall-complaint`"
                classString="lni-u-mt2"
                :complaint="overallComplaint"
            >
                <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                    {{ content?.complaintPrefix }}
                </p>
                <span class="--label">
                    {{ overallComplaint ? '' : content?.notText }} {{ content?.workloadComplaintText }}
                </span>
            </lni-janitorial-complaint-card>
            <lni-janitorial-discomfort-level
                v-if="isErgonomicEvaluation"
                :id="`${id}_overall_discomfort`"
                classString="lni-u-mt2"
                :name="content?.workloadLabel"
                :shiftStartDiscomfort="overallDiscomfortStart"
                :shiftEndDiscomfort="overallDiscomfortEnd"
            />
            <h3 class="lni-u-heading--3 lni-u-mt3">
                {{ content?.workloadCalculationHeading }}
            </h3>
            <div class="lni-u-reading-width--max lni-u-mt1">
                {{ content?.workloadTableDescription }}
            </div>
            <lni-table
                v-if="isErgonomicEvaluation"
                :id="`${id}_table`"
                class="lni-u-mb3"
                :hidePagingActions="true"
                :caption="content?.workloadTableCaption"
                :summary="content?.workloadTableSummary"
                :striped="true"
                :items="items"
                loadState="loaded"
                :sort="{
                    index: 'asc',
                }"
                :columns="columns"
            >
                <template
                    slot="body"
                    slot-scope="props"
                >
                    <tr>
                        <td class="lni-u-text--center">
                            {{ props.item.index }}
                        </td>
                        <td class="lni-u-text--left">
                            {{ props.item.assignment }}
                        </td>
                        <td class="lni-u-text--center">
                            {{ props.item.steps }}
                        </td>
                        <td class="lni-u-text--center">
                            {{ props.item.energy }}
                        </td>
                        <td class="lni-u-text--center">
                            {{ props.item.mawt }}
                        </td>
                    </tr>
                </template>
            </lni-table>
            <lni-table
                v-else
                :id="`${id}_table`"
                class="lni-u-mb3"
                :hidePagingActions="true"
                :caption="content?.workloadCalculationTableCaption"
                :summary="content?.workloadCalculationTableSummary"
                :striped="true"
                :items="items"
                loadState="loaded"
                :sort="{
                    index: 'asc',
                }"
                :columns="columns"
            >
                <template
                    slot="body"
                    slot-scope="props"
                >
                    <tr>
                        <td class="lni-u-text--center">
                            {{ props.item.index }}
                        </td>
                        <td class="lni-u-text--left">
                            {{ props.item.assignment }}
                        </td>
                        <td class="lni-u-text--center">
                            {{ props.item.steps }}
                        </td>
                        <td class="lni-u-text--center">
                            {{ props.item.recoveryTime }}
                        </td>
                    </tr>
                </template>
            </lni-table>
        </template>
    </lni-disclosure>
</template>

<script>
import {
    mapGetters,
} from 'vuex';

import {
    mawtRatio,
    mawtRatioTotal,
    recoveryTime,
    steps,
    stepsTotal,
} from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/calculations';

import {
    assignmentText,
    formatNumber,
    toFixedOne,
} from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/helpers';

import LniJanitorialComplaintCard from '@gov.wa.lni/application.janitorial-workload-calculator/source/components/shared/lni-janitorial-complaint-card.component.vue';
import LniJanitorialDiscomfortLevel from '@gov.wa.lni/application.janitorial-workload-calculator/source/components/shared/lni-janitorial-discomfort-level.component.vue';
import LniJanitorialInfoModal from '@gov.wa.lni/application.janitorial-workload-calculator/source/components/shared/lni-janitorial-info-modal.component.vue';

export default {
    name: 'LniJanitorialOverallWorkload',
    components: {
        LniJanitorialComplaintCard,
        LniJanitorialDiscomfortLevel,
        LniJanitorialInfoModal,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        jobPlan: {
            type: Array,
            required: true,
        },
        lowerExtremityDiscomfortEnd: {
            type: Number,
            default: -1,
        },
        lowerExtremityDiscomfortStart: {
            type: Number,
            default: -1,
        },
        overallComplaint: {
            type: Boolean,
            default: false,
        },
        overallDiscomfortEnd: {
            type: Number,
            default: -1,
        },
        overallDiscomfortStart: {
            type: Number,
            default: -1,
        },
        walkingComplaint: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['toggle'],
    computed: {
        cdcStepText() {
            const max = 15000;
            const min = 7000;
            const totalSteps = Number(this.stepsTotal.replace(/,/g, ''));
            let result = this.content?.stepsModerateText;
            if (totalSteps > max) {
                result = this.content?.stepsHighText;
            }
            if (totalSteps < min) {
                result = this.content?.stepsLowText;
            }
            return result;
        },
        columns() {
            let result = [
                {
                    source: 'steps',
                    displayText: this.content?.stepsText,
                },
            ];

            if (this.isErgonomicEvaluation) {
                result = [
                    ...result,
                    {
                        source: 'energy',
                        displayText: this.content?.energyText,
                    },{
                        source: 'mawt',
                        displayText: this.content?.mawtHeading,
                    },
                ];
            } else {
                result.push({
                    source: 'recoveryTime',
                    displayText: this.content?.recoveryTimeText,
                });
            }

            return this.tableColumns(result);
        },
        items() {
            let result;
            if (this.isErgonomicEvaluation) {
                result = this.jobPlan.map((assignment, index) => {
                    const mawt = mawtRatio(assignment);
                    return {
                        index: index + 1,
                        assignment: assignmentText(assignment),
                        steps: formatNumber(steps(assignment)),
                        energy: this.energyText(mawt),
                        mawt: toFixedOne(mawt),
                    };
                });
            } else {
                result = this.jobPlan.map((assignment, index) => {
                    const recovery = recoveryTime(assignment);
                    return {
                        index: index + 1,
                        assignment: assignmentText(assignment),
                        steps: formatNumber(steps(assignment)),
                        recoveryTime: this.recoveryTimeText(recovery),
                    };
                });
            }

            return result;
        },
        mawtRatioTotal() {
            return mawtRatioTotal(this.jobPlan);
        },
        stepsTotal() {
            return formatNumber(stepsTotal(this.jobPlan));
        },
        ...mapGetters({
            content: 'appState/content',
            isErgonomicEvaluation: 'appState/isErgonomicEvaluation',
            isManagementReport: 'appState/isManagementReport',
            tableColumns: 'appState/tableColumns',
        }),
    },
    mounted() {
        this.$nextTick(() => {
            this.$store.commit('setAttribute', {
                id: `${this.id}_table`,
                attribute: 'items',
                value: this.items,
            }, {
                root: true,
            });
            this.$store.commit('setAttribute', {
                id: `${this.id}_table`,
                attribute: 'totalItems',
                value: this.items.length,
            }, {
                root: true,
            });
        });
    },
    methods: {
        energyText(mawtRatio) {
            const min = 0.8;
            let result = this.content?.moderateText;
            if (mawtRatio > 1) {
                result = this.content?.lowText;
            }
            if (mawtRatio < min) {
                result = this.content?.highText;
            }
            return result;
        },
        onToggle() {
            this.$emit('toggle');
        },
        recoveryTimeText(time) {
            if (Number(time) < 1) {
                return this.content?.insufficientText;
            } else {
                return this.content?.sufficientText;
            }
        },
    },
};
</script>